/**
 * PipelineView — Visual pipeline showing items per status
 */

import clsx from 'clsx';
import type { PipelineStatus } from '../../stores/wasserij-store';

interface PipelineViewProps {
  pipeline: PipelineStatus[];
  total: { items: number; m2: number };
  isLoading: boolean;
  onStatusClick: (status: string) => void;
}

// Define the order and grouping of statuses
const mainFlow = [
  'Klaar voor Voorinspectie',
  'Klaar voor Reinigen',
  'Drogen',
  'In Nabehandeling',
];

const sideFlow = [
  'In Reparatie',
  'Randen Plakken',
];

const outputFlow = [
  'Ingepakt',
  'Klaargelegd',
  'In bus',
];

const shortLabels: Record<string, string> = {
  'Klaar voor Voorinspectie': 'Voorinsp.',
'Klaar voor Reinigen': 'Reinigen',
  'Drogen': 'Drogen',
  'In Nabehandeling': 'Nabehand.',
  'In Reparatie': 'Reparatie',
  'Randen Plakken': 'Randen',
  'Ingepakt': 'Ingepakt',
  'Klaargelegd': 'Klaargelegd',
  'In bus': 'In bus',
};

function StatusBlock({ status, onStatusClick }: { status: PipelineStatus; onStatusClick: (s: string) => void }) {
  const isLarge = status.itemCount > 50;
  const isMedium = status.itemCount > 10;

  return (
    <button
      onClick={() => onStatusClick(status.status)}
      className={clsx(
        'flex flex-col items-center justify-center rounded-xl border-2 transition-all hover:shadow-md hover:scale-[1.02] active:scale-[0.98] cursor-pointer',
        isLarge && 'bg-indigo-50 border-indigo-200 min-w-[100px] min-h-[90px] p-3',
        isMedium && !isLarge && 'bg-slate-50 border-slate-200 min-w-[85px] min-h-[80px] p-2.5',
        !isMedium && 'bg-white border-slate-200 min-w-[75px] min-h-[70px] p-2'
      )}
    >
      <span className={clsx(
        'font-bold',
        isLarge ? 'text-2xl text-indigo-700' : isMedium ? 'text-xl text-slate-700' : 'text-lg text-slate-600'
      )}>
        {status.itemCount}
      </span>
      <span className="text-xs text-slate-500 mt-0.5">
        {Math.round(status.totalM2)} m²
      </span>
      <span className="text-[10px] text-slate-400 mt-1 font-medium">
        {shortLabels[status.status] || status.status}
      </span>
    </button>
  );
}

export function PipelineView({ pipeline, total, isLoading, onStatusClick }: PipelineViewProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const pipelineMap = new Map(pipeline.map(s => [s.status, s]));
  const getStatus = (name: string): PipelineStatus => pipelineMap.get(name) || { status: name, itemCount: 0, totalM2: 0 };

  return (
    <div>
      {/* Main flow */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {mainFlow.map((name, i) => (
          <div key={name} className="flex items-center gap-2">
            <StatusBlock status={getStatus(name)} onStatusClick={onStatusClick} />
            {i < mainFlow.length - 1 && (
              <svg className="w-4 h-4 text-slate-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </div>
        ))}
      </div>

      {/* Side flow + output flow */}
      <div className="flex flex-wrap gap-6">
        <div className="flex items-center gap-2">
          {sideFlow.map(name => (
            <StatusBlock key={name} status={getStatus(name)} onStatusClick={onStatusClick} />
          ))}
        </div>

        <div className="flex items-center gap-2">
          {outputFlow.map((name, i) => (
            <div key={name} className="flex items-center gap-2">
              <StatusBlock status={getStatus(name)} onStatusClick={onStatusClick} />
              {i < outputFlow.length - 1 && (
                <svg className="w-4 h-4 text-slate-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Total */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <p className="text-sm text-slate-500">
          Totaal in wasserij: <span className="font-semibold text-slate-700">{total.items.toLocaleString('nl-NL')}</span> items
          {' '}&middot;{' '}
          <span className="font-semibold text-slate-700">{total.m2.toLocaleString('nl-NL')}</span> m²
        </p>
      </div>
    </div>
  );
}
